import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getApiBaseUrl } from "../config/api";
import { applyNotFoundSeo, clearNotFoundSeo } from "../utils/notfoundSeo";
import notFoundHero from "../assets/404-hero.jpg";

const PAGE_GUTTER = "px-5 md:px-32 lg:px-40";
const HELPFUL_LINKS = [
  { to: "/category", label: "Product Categories", icon: "box", name: "Browse Product Categories" },
  { to: "/products", label: "All Products", icon: "doc", name: "Browse All Products" },
  { to: "/deliveries", label: "Recent Deliveries", icon: "truck", name: "View Recent Deliveries" },
  { to: "/contact", label: "Contact Us", icon: "phone", name: "Contact Us" },
];

function lockDoubleClick(event) {
  const el = event.currentTarget;
  if (el.dataset.navLock === "1") {
    event.preventDefault();
    return;
  }
  el.dataset.navLock = "1";
  window.setTimeout(() => {
    delete el.dataset.navLock;
  }, 700);
}

function prefetchPath(path) {
  if (typeof document === "undefined") return;
  const href = String(path || "");
  if (!href.startsWith("/")) return;
  if (document.head.querySelector(`link[rel="prefetch"][href="${href}"]`)) return;
  if (!document.createElement("link").relList?.supports?.("prefetch")) return;
  const link = document.createElement("link");
  link.rel = "prefetch";
  link.href = href;
  document.head.appendChild(link);
}

function LinkIcon({ name }) {
  const common = {
    viewBox: "0 0 24 24",
    className: "notfound-link-icon h-5 w-5 shrink-0 text-at-dark",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.6",
    strokeLinejoin: "round",
    strokeLinecap: "round",
    "aria-hidden": true,
  };
  if (name === "truck") {
    return (
      <svg {...common}>
        <path d="M3 8h12v8H3z" />
        <path d="M15 11h3.5L21 14v2h-6" />
        <circle cx="7" cy="17.5" r="1.4" />
        <circle cx="17.5" cy="17.5" r="1.4" />
      </svg>
    );
  }
  if (name === "doc") {
    return (
      <svg {...common}>
        <path d="M7 3.5h8l4 4V20.5H7z" />
        <path d="M15 3.5V8h4.5" />
        <path d="M10 12h6M10 15.5h6" />
      </svg>
    );
  }
  if (name === "phone") {
    return (
      <svg {...common}>
        <path d="M7.2 3.8c.6-.7 1.7-.6 2.2.2l1.1 1.8c.4.7.2 1.5-.4 2l-1.1.9c.8 1.8 2.2 3.2 4 4l.9-1.1c.5-.6 1.3-.8 2-.4l1.8 1.1c.8.5.9 1.6.2 2.2l-.8.8c-.8.8-2.1 1.1-3.2.6C9.2 16.6 5.4 12.8 4.1 7.8c-.5-1.1-.2-2.4.6-3.2l.8-.8z" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M12 3.5l7.5 4.3v8.4L12 20.5l-7.5-4.3V7.8L12 3.5z" />
      <path d="M12 12l7.5-4.2M12 12v8.5M12 12L4.5 7.8" />
    </svg>
  );
}

function NotFoundPage() {
  const headingRef = useRef(null);
  const heroRef = useRef(null);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [siteName, setSiteName] = useState("");

  useLayoutEffect(() => {
    applyNotFoundSeo({ siteName });
    return clearNotFoundSeo;
  }, [siteName]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (cancelled) return;
        const contact = Array.isArray(list) && list[0] ? list[0] : null;
        setSiteName(String(contact?.siteName || "").trim());
      })
      .catch(() => {
        if (!cancelled) setSiteName("");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const image = heroRef.current;
    if (image?.complete && image.naturalWidth) setHeroLoaded(true);
  }, []);

  return (
    <div className="notfound-page">
      <section className="relative overflow-hidden bg-at-dark text-white">
        <img
          ref={heroRef}
          src={notFoundHero}
          alt=""
          width={1600}
          height={600}
          fetchPriority="high"
          onLoad={() => setHeroLoaded(true)}
          className={`notfound-hero pointer-events-none absolute inset-0 h-full w-full object-cover object-center${heroLoaded ? " is-loaded" : ""}`}
        />
        <div className="notfound-overlay pointer-events-none absolute inset-0 bg-black/35 md:bg-black/15" />
        <div className="notfound-overlay pointer-events-none absolute inset-y-0 left-0 h-full w-full bg-gradient-to-r from-black/80 via-black/50 to-transparent md:w-[62%]" />
        <div className={`relative z-10 flex min-h-[28rem] flex-col justify-center py-14 md:min-h-[32rem] md:py-16 ${PAGE_GUTTER}`}>
          <div className="max-w-xl">
            <p className="notfound-in notfound-in-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-at-gold">
              Page not found
            </p>
            <p className="notfound-numeral notfound-in-404 mt-3 font-serif text-7xl leading-none md:text-8xl" aria-hidden="true">
              404
            </p>
            <h1
              ref={headingRef}
              tabIndex={-1}
              className="notfound-in notfound-in-2 mt-4 font-serif text-3xl uppercase leading-tight md:text-5xl"
            >
              We couldn&apos;t
              <br />
              find that page
            </h1>
            <p className="notfound-in notfound-in-3 mt-4 max-w-md text-sm leading-relaxed text-white/75">
              The page may have moved, the link may be outdated, or the product is no longer available.
            </p>
            <div className="notfound-in notfound-in-4 mt-8 flex flex-wrap gap-3">
              <Link
                to="/"
                aria-label="Back to Home"
                onClick={lockDoubleClick}
                onMouseEnter={() => prefetchPath("/")}
                onFocus={() => prefetchPath("/")}
                className="hero-btn inline-flex items-center gap-2 rounded-sm bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
              >
                Back to Home <span className="btn-arrow">→</span>
              </Link>
              <Link
                to="/products"
                aria-label="Browse All Products"
                onClick={lockDoubleClick}
                onMouseEnter={() => prefetchPath("/products")}
                onFocus={() => prefetchPath("/products")}
                className="notfound-products-btn hero-btn inline-flex items-center gap-2 rounded-sm border border-white/80 bg-transparent px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10"
              >
                Browse All Products
              </Link>
            </div>
            <p className="notfound-in notfound-in-5 mt-6 text-sm text-white/80">
              Need help finding a product?{" "}
              <Link
                to="/contact"
                aria-label="Contact Our Team"
                onClick={lockDoubleClick}
                className="notfound-contact font-semibold text-at-gold hover:text-at-gold-hover"
              >
                Contact Our Team <span className="btn-arrow">→</span>
              </Link>
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-neutral-200 bg-white" aria-label="Helpful Links">
        <div className={`flex flex-col py-2 lg:flex-row lg:items-stretch ${PAGE_GUTTER}`}>
          <p className="shrink-0 py-4 text-sm font-semibold text-at-dark lg:flex lg:items-center lg:pr-8">
            Helpful Links
          </p>
          <ul className="flex flex-1 flex-col lg:flex-row lg:items-stretch">
            {HELPFUL_LINKS.map((item) => (
              <li
                key={item.to}
                className="border-t border-neutral-200 lg:flex lg:flex-1 lg:border-t-0 lg:border-l"
              >
                <Link
                  to={item.to}
                  aria-label={item.name}
                  onClick={lockDoubleClick}
                  onMouseEnter={() => prefetchPath(item.to)}
                  onFocus={() => prefetchPath(item.to)}
                  className="notfound-link flex min-h-[44px] w-full items-center gap-2 py-4 text-sm font-medium text-at-dark lg:justify-center lg:px-5"
                >
                  <LinkIcon name={item.icon} />
                  {item.label}
                  <span className="btn-arrow text-at-gold" aria-hidden="true">
                    →
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}

export default NotFoundPage;
